import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import moment from 'moment-timezone';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import { Tooltip, Typography } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import { VFGlobalContext } from '@mummssoftware/global-context';
import {
  CalendarContainer,
  EventCalendarHeader,
  EventCalendarLayout,
  HeaderFilterContainer,
  HeaderFilterLabel,
  HeaderFilterSelect,
  HeaderFiltersRow,
} from '../../components/layout/WrapperDiv';
import { AppContext } from '../../context';
import {
  getRoleGroupList,
  getRoleGroupsForFilter,
  getFullCalendarDateKey,
  isDateKeyInRange,
  parseStartOfWeekIndex,
} from '../data';
import { HighlightedPeriod, HighlightState } from '../data/types';
import { SET_CALENDAR_DATES } from '../context/constants';
import { ScheduledVisitsContext } from '../context/ScheduledVisitsContext';
import { CalendarLoadingOverlay } from './CalendarLoadingOverlay';
import { DayCellRenderer } from './DayRenderer';
import { EventModal, EventModalData } from './EventModal';
import {
  getPlanColumnMountPoints,
  injectPlanColumn,
  PlanColumnMountPoints,
  PlanColumnPortals,
} from './PlanColumn';

const SMALL_WIDGET_VIEWS = {
  dayGridFourDay: {
    type: 'dayGrid',
    duration: { days: 4 },
    dateAlignment: 'day',
  },
};

export const EventCalendar = () => {
  const { smallWidget, currentUser, initialDate } = useContext(AppContext);
  const {
    dispatch,
    calendarDates,
    calendarViewData,
    carePlanFrequenciesForRange,
    isCalendarLoading,
  } = useContext(ScheduledVisitsContext);
  const siteTimezone = currentUser?.siteTimezone || moment.tz.guess();
  const weekStartDayIndex = parseStartOfWeekIndex(
    currentUser?.startOfWeek ?? null,
  );
  const { roleGroupsGlobal: vfRoleGroups } = useContext(VFGlobalContext);
  // OP-33263: standalone calendars load their own role groups.
  const { roleGroups: contextRoleGroups } = useContext(ScheduledVisitsContext);
  const roleGroupsGlobal = vfRoleGroups ?? contextRoleGroups;

  const [filteredRoleGroups, setFilteredRoleGroups] = useState<
    mumms.RoleGroup[]
  >([]);
  const [filteringRoleGroups, setFilteringRoleGroups] = useState<number[]>([0]);
  const [eventModalOpen, setEventModalOpen] = useState<EventModalData | null>(
    null,
  );
  const [planMounts, setPlanMounts] = useState<PlanColumnMountPoints | null>(
    null,
  );
  const [highlightState, setHighlightState] = useState<HighlightState | null>(
    null,
  );

  /** Called when a dot is clicked: highlight exactly its one admin period. */
  const toggleDotHighlight = useCallback(
    (
      cpfId: number,
      periodStart: string,
      periodEnd: string,
      planBoxWeekKey: string,
    ) => {
      setHighlightState((prev) =>
        prev?.cpfId === cpfId && prev?.planBoxWeekKey === planBoxWeekKey
          ? null
          : {
              cpfId,
              planBoxWeekKey,
              ranges: [{ start: periodStart, end: periodEnd }],
            },
      );
    },
    [],
  );

  /** Called when a plan box is clicked: highlight all its contributing periods. */
  const toggleBoxHighlight = useCallback(
    (cpfId: number, planBoxWeekKey: string, periods: HighlightedPeriod[]) => {
      setHighlightState((prev) =>
        prev?.cpfId === cpfId && prev?.planBoxWeekKey === planBoxWeekKey
          ? null
          : { cpfId, planBoxWeekKey, ranges: periods },
      );
    },
    [],
  );

  const calendarRef = useRef<FullCalendar>(null);
  const calendarContainerRef = useRef<HTMLDivElement>(null);

  // ── Plan column injection ─────────────────────────────────────────────────

  const injectColumn = useCallback(() => {
    if (smallWidget) {
      setPlanMounts(null);
      return;
    }
    // Double rAF: wait for FC updateSize/layout to apply col widths first.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const fc = calendarContainerRef.current?.querySelector(
          '.fc',
        ) as HTMLElement | null;
        injectPlanColumn(fc);
        setPlanMounts(getPlanColumnMountPoints(fc));
      });
    });
  }, [smallWidget]);

  // Once calendarViewData is populated, remeasure then re-inject so column
  // widths are stable (dots have been laid out at this point).
  const hasViewData = !!calendarViewData;
  const syncedRef = useRef(false);

  useEffect(() => {
    if (isCalendarLoading) {
      syncedRef.current = false;
    }
  }, [isCalendarLoading]);

  useEffect(() => {
    if (!hasViewData || syncedRef.current) return undefined;
    syncedRef.current = true;
    const id = requestAnimationFrame(() => {
      const api = calendarRef.current?.getApi();
      if (api) {
        api.updateSize();
        api.gotoDate(api.getDate());
      }
      injectColumn();
    });
    return () => cancelAnimationFrame(id);
  }, [hasViewData, injectColumn]);

  // Re-inject when month navigates or loading finishes.
  useEffect(() => {
    if (smallWidget || isCalendarLoading) return;
    injectColumn();
  }, [calendarDates, isCalendarLoading, injectColumn, smallWidget]);

  // Re-inject on container resize (widget expand/collapse).
  useEffect(() => {
    if (smallWidget) return undefined;
    const container = calendarContainerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver(() => {
      const api = calendarRef.current?.getApi();
      if (api) api.updateSize();
      injectColumn();
    });
    observer.observe(container);

    // The dashboard's expanded widget animates in with transform: scale(),
    // which FC measures (rows and columns come out scaled) but which never
    // changes layout size, so the observer above does not fire. Remeasure
    // when an ancestor's animation or transition ends.
    const onSettled = (event: Event) => {
      const target = event.target as Node | null;
      if (!target || !target.contains(container)) return;
      const api = calendarRef.current?.getApi();
      if (api) api.updateSize();
      injectColumn();
    };
    document.addEventListener('animationend', onSettled);
    document.addEventListener('transitionend', onSettled);
    return () => {
      observer.disconnect();
      document.removeEventListener('animationend', onSettled);
      document.removeEventListener('transitionend', onSettled);
    };
  }, [injectColumn, smallWidget]);

  // ── Filters ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (carePlanFrequenciesForRange && roleGroupsGlobal) {
      setFilteredRoleGroups(
        getRoleGroupsForFilter(carePlanFrequenciesForRange, roleGroupsGlobal),
      );
    }
  }, [carePlanFrequenciesForRange, roleGroupsGlobal]);

  const updateCalendarDates = ({
    startStr,
    endStr,
  }: {
    startStr: string;
    endStr: string;
  }) => {
    if (
      calendarDates &&
      calendarDates[0] === startStr &&
      calendarDates[1] === endStr
    ) {
      injectColumn();
      return;
    }
    dispatch({ type: SET_CALENDAR_DATES, payload: [startStr, endStr] });
  };

  const onSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as number[];
    if (value[value.length - 1] === 0) {
      setFilteringRoleGroups([0]);
      return;
    }
    setFilteringRoleGroups(value.filter((option) => option !== 0));
  };

  return (
    <EventCalendarLayout>
      <EventModal
        modalOpen={eventModalOpen}
        closeModal={() => setEventModalOpen(null)}
      />

      {!smallWidget && (
        <EventCalendarHeader>
          <HeaderFiltersRow>
            <HeaderFilterContainer>
              <HeaderFilterLabel>
                Select role group to filter:
              </HeaderFilterLabel>
              <HeaderFilterSelect
                value={
                  (filteringRoleGroups.length && filteringRoleGroups) || [0]
                }
                renderValue={(value) =>
                  getRoleGroupList(
                    (value as number[]) ?? [0],
                    roleGroupsGlobal ?? [],
                  )
                }
                onChange={onSelectChange}
                multiple
              >
                {[...filteredRoleGroups, { name: 'All', id: 0 }].map(
                  (option) => (
                    <MenuItem key={option.id} value={option.id}>
                      <Tooltip title={option.name}>
                        <Typography>{option.name}</Typography>
                      </Tooltip>
                    </MenuItem>
                  ),
                )}
              </HeaderFilterSelect>
            </HeaderFilterContainer>
          </HeaderFiltersRow>
        </EventCalendarHeader>
      )}

      <CalendarContainer $smallWidget={smallWidget} ref={calendarContainerRef}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin]}
          timeZone={siteTimezone}
          dayCellClassNames={(arg) => {
            if (!highlightState) return [];
            const dateKey = getFullCalendarDateKey(arg.date);
            return highlightState.ranges.some((r) =>
              isDateKeyInRange(dateKey, r.start, r.end),
            )
              ? ['sv-period-highlight']
              : [];
          }}
          firstDay={weekStartDayIndex}
          initialDate={initialDate}
          initialView={smallWidget ? 'dayGridFourDay' : 'dayGridMonth'}
          views={smallWidget ? SMALL_WIDGET_VIEWS : undefined}
          headerToolbar={
            smallWidget
              ? { left: '', center: 'title', right: 'today' }
              : { left: '', center: 'title', right: 'prev,next,today' }
          }
          dayCellContent={(props) => (
            <DayCellRenderer
              calendarViewData={calendarViewData}
              setModalOpen={setEventModalOpen}
              filteringRoleGroups={filteringRoleGroups}
              onDotHighlight={toggleDotHighlight}
              {...props}
            />
          )}
          datesSet={updateCalendarDates}
          selectable
          height="100%"
          // Stretch week rows to the view height (otherwise they size to content
          // and leave a dead band under the grid in a tall expanded widget).
          expandRows
          displayEventTime={false}
          dayMaxEvents
        />
        {isCalendarLoading && <CalendarLoadingOverlay />}
      </CalendarContainer>

      {!smallWidget && (
        <PlanColumnPortals
          mountPoints={planMounts}
          calendarViewData={calendarViewData}
          filteringRoleGroups={filteringRoleGroups}
          highlightState={highlightState}
          onBoxHighlight={toggleBoxHighlight}
        />
      )}
    </EventCalendarLayout>
  );
};
